import { useState, useCallback, useRef } from "react";
import { getAllMenus, updateDishStatus } from "../services/menu-service.js";
import { getTranslation } from "../utils/translate.js";
import { useLanguage } from "../utils/languageContext.js";

const SEARCH_FETCH_LIMIT = 10000;

/**
 * Match dish names in Armenian, Russian, and English.
 * @param {Object} dish
 * @param {string} query
 * @returns {boolean}
 */
const dishMatchesSearch = (dish, query) => {
    const q = (query || "").trim().toLowerCase();
    if (!q) {
        return true;
    }
    return [dish?.nameHy, dish?.nameRu, dish?.nameEn].some((name) =>
        (name || "").toLowerCase().includes(q)
    );
};

/**
 * Custom hook for managing menu/dishes
 * @param {Object} options - Configuration options
 * @param {string} options.chefId - Chef ID
 * @param {number} options.itemsPerPage - Items per page (default: 20)
 * @param {string|null} [options.statusFilter] - Pass to API as status (e.g. ACTIVE); omit when null
 * @param {string} [options.searchQuery] - Dish name search across Hy/Ru/En
 * @param {Object} options.toastRef - Reference to Toast component
 * @returns {Object} Menu state and methods
 */
export const useMenu = ({ chefId, itemsPerPage = 20, statusFilter = null, searchQuery = "", toastRef }) => {
    const { language } = useLanguage();
    const [menuItems, setMenuItems] = useState([]);
    const [totalItems, setTotalItems] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const requestIdRef = useRef(0);

    const fetchMenus = useCallback(
        async (page = 1) => {
            if (!chefId) return;

            const requestId = ++requestIdRef.current;
            setLoading(true);
            setError(null);

            try {
                const query = (searchQuery || "").trim();
                const status = statusFilter || undefined;

                if (query) {
                    const data = await getAllMenus(chefId, 0, SEARCH_FETCH_LIMIT, { status });
                    if (requestId !== requestIdRef.current) return;

                    const filtered = (data?.dishDtoTreeSet || []).filter((dish) =>
                        dishMatchesSearch(dish, query)
                    );
                    const offset = (page - 1) * itemsPerPage;
                    setMenuItems(filtered.slice(offset, offset + itemsPerPage));
                    setTotalItems(filtered.length);
                    setError(null);
                    return;
                }

                const offset = (page - 1) * itemsPerPage;
                const data = await getAllMenus(chefId, offset, itemsPerPage, { status });
                if (requestId !== requestIdRef.current) return;

                setMenuItems(data?.dishDtoTreeSet || []);
                setTotalItems(data?.count || 0);
                setError(null);
            } catch (error) {
                if (requestId !== requestIdRef.current) return;
                console.error("Error fetching menus:", error);
                setError(error);
                setMenuItems([]);
                setTotalItems(0);
            } finally {
                if (requestId === requestIdRef.current) {
                    setLoading(false);
                }
            }
        },
        [chefId, itemsPerPage, statusFilter, searchQuery]
    );

    const updateStatus = useCallback(
        async (dishId, status) => {
            try {
                await updateDishStatus(dishId, status);
                toastRef?.current?.addToast(
                    getTranslation("menu.statusUpdated", language) || "Dish status updated successfully",
                    "success"
                );
                // Refresh menu after status update
                await fetchMenus();
            } catch (error) {
                console.error("Failed to update dish status:", error);
                toastRef?.current?.addToast(
                    getTranslation("menu.statusUpdateFailed", language) || "Failed to update dish status",
                    "error"
                );
                throw error;
            }
        },
        [fetchMenus, language, toastRef]
    );

    return {
        menuItems,
        totalItems,
        loading,
        error,
        fetchMenus,
        updateStatus
    };
};

